/**
 * 远程组件定义注册表
 * 支持分三阶段加载：
 *   1. 基础组件（scope=base）— 应用启动时加载
 *   2. 店铺基础组件（scope=storeBase）— 应用启动时加载
 *   3. 租户组件（scope=tenant）— 进入编辑器时加载
 *
 * 使用 Vue 响应式版本计数器，确保依赖 getComponentDefinition() 的 computed
 * 在注册表更新后自动重新计算（普通 Map 不被 Vue 追踪）。
 */
import { ref } from 'vue'
import type { ComponentDefinition, ComponentScope } from './componentDefinition'

/** 每次有新组件写入注册表时递增，使依赖此值的 computed 自动失效 */
const registryVersion = ref(0)
const REGISTRY_TTL_MS = 60 * 1000
const registryByDomain = new Map<string, Map<string, ComponentDefinition>>()
const baseLoadedByDomain = new Map<string, boolean>()
const storeBaseLoadedByDomain = new Map<string, boolean>()
const tenantLoadedByDomain = new Map<string, boolean>()
const baseLoadedAtByDomain = new Map<string, number>()
const storeBaseLoadedAtByDomain = new Map<string, number>()
const tenantLoadedAtByDomain = new Map<string, number>()
const baseLoadPromiseByDomain = new Map<string, Promise<void>>()
const storeBaseLoadPromiseByDomain = new Map<string, Promise<void>>()
const tenantLoadPromiseByDomain = new Map<string, Promise<void>>()
let activeDomainKey = ''
let activeLangKey = 'en'

function parseItem(item: any): ComponentDefinition {
  return {
    component: item.component,
    displayName: item.displayName,
    icon: item.icon,
    category: item.category,
    scope: item.scope ?? 'base',
    pageZone: item.pageZone,
    sectionCategory: item.sectionCategory,
    sectionComponent: item.sectionComponent,
    blockCategory: item.blockCategory,
    blockComponents: item.blockComponents,
    htmlTemplate: item.htmlTemplate,
    css: item.css,
    hasChildren: item.hasChildren ?? undefined,
    jsLogic: item.jsLogic ?? undefined,
    schema: item.schemaFields ?? item.schema ?? [],
    defaultVariables: item.defaultVariables ?? {},
    behavior: item.behavior ?? undefined,
    dataSource: item.dataSource ?? undefined,
    slots: item.slots ?? undefined,
    addableChildren: item.addableChildren ?? undefined,
    autoFetch: item.autoFetch ?? undefined,
    globalStyleConfig: item.globalStyleConfig ?? undefined,
  }
}

type RequestHeaders = Record<string, string>

function withNoCacheHeaders(headers?: RequestHeaders): RequestHeaders {
  return {
    ...(headers || {}),
    'cache-control': 'no-cache',
    pragma: 'no-cache',
  }
}

function normalizeDomainKey(domain?: string): string {
  return String(domain || '').trim().toLowerCase()
}

function getDomainKeyFromHeaders(headers?: RequestHeaders): string {
  const raw = String(headers?.['x-forwarded-host'] || headers?.['host'] || '').split(',')[0].trim()
  return normalizeDomainKey(raw)
}

function getLangKeyFromHeaders(headers?: RequestHeaders): string {
  return String(headers?.['x-soho-route-lang'] || headers?.['lang'] || 'en').trim().toLowerCase() || 'en'
}

function buildRegistryKey(domain?: string, lang?: string): string {
  const domainKey = normalizeDomainKey(domain)
  const langKey = String(lang || 'en').trim().toLowerCase() || 'en'
  return `${domainKey}::${langKey}`
}

function getRegistry(domainKey: string): Map<string, ComponentDefinition> {
  const existing = registryByDomain.get(domainKey)
  if (existing) return existing
  const next = new Map<string, ComponentDefinition>()
  registryByDomain.set(domainKey, next)
  return next
}

function getActiveDomainKey(): string {
  return buildRegistryKey(activeDomainKey, activeLangKey)
}

export function setActiveDomain(domain?: string, lang?: string): void {
  activeDomainKey = normalizeDomainKey(domain)
  activeLangKey = String(lang || activeLangKey || 'en').trim().toLowerCase() || 'en'
}

function isFreshLoaded(loadedAtMs: number | undefined): boolean {
  if (!loadedAtMs) return false
  return Date.now() - loadedAtMs < REGISTRY_TTL_MS
}

async function loadByScope(scope: ComponentScope, apiBase: string = '', requestHeaders?: RequestHeaders) {
  try {
    console.log(`[remoteComponentRegistry] ➜ ${apiBase}/api/cms/components scope=${scope}`)

    const response = await $fetch<{ code: number; data: any[] }>(
      `${apiBase}/api/cms/components`,
      {
        query: { scope, lang: getLangKeyFromHeaders(requestHeaders), _scopeCacheKey: 'strict-v1' },
        headers: withNoCacheHeaders(requestHeaders),
        cache: 'no-store',
      },
    )

    console.log(`[remoteComponentRegistry] ✔ scope=${scope} code:`, response?.code, 'data count:', response?.data?.length ?? 'N/A')

    if (!response || !Array.isArray(response.data)) {
      console.error(`[CMS] 加载 ${scope} 组件定义失败: 响应格式异常`, response)
      return 0
    }

    const domainKey = getActiveDomainKey() || buildRegistryKey(getDomainKeyFromHeaders(requestHeaders), getLangKeyFromHeaders(requestHeaders))
    const registry = getRegistry(domainKey)
    const scopedItems = response.data.filter((item) => (item?.scope ?? 'base') === scope)
    const droppedCount = response.data.length - scopedItems.length
    if (droppedCount > 0) {
      console.error(`[CMS] 丢弃 ${droppedCount} 个 scope 不匹配的组件定义，期望 scope=${scope}`)
    }

    let count = 0
    for (const item of scopedItems) {
      const definition = parseItem(item)
      registry.set(definition.component, definition)
      count++
    }
    if (count > 0) registryVersion.value++
    return count
  } catch (error) {
    console.error(`[CMS] 加载 ${scope} 组件定义异常:`, error)
    return 0
  }
}

/**
 * 加载基础组件（应用启动时调用）。
 * 使用 in-flight promise 防止并发重复请求。
 */
export async function loadBaseComponents(apiBase: string = '', requestHeaders?: RequestHeaders) {
  const domainKey = getActiveDomainKey() || buildRegistryKey(getDomainKeyFromHeaders(requestHeaders), getLangKeyFromHeaders(requestHeaders))
  if (baseLoadedByDomain.get(domainKey) && isFreshLoaded(baseLoadedAtByDomain.get(domainKey))) return
  const inFlight = baseLoadPromiseByDomain.get(domainKey)
  if (inFlight) return inFlight
  const promise = (async () => {
    const count = await loadByScope('base', apiBase, requestHeaders)
    baseLoadedByDomain.set(domainKey, true)
    baseLoadedAtByDomain.set(domainKey, Date.now())
    console.log(`[CMS] 基础组件加载完成，共 ${count} 个`)
  })()
  baseLoadPromiseByDomain.set(domainKey, promise)
  try {
    await promise
  } finally {
    baseLoadPromiseByDomain.delete(domainKey)
  }
}

/**
 * 加载店铺基础组件。
 * 使用 in-flight promise 防止并发重复请求。
 */
export async function loadStoreBaseComponents(apiBase: string = '', requestHeaders?: RequestHeaders) {
  const domainKey = getActiveDomainKey() || buildRegistryKey(getDomainKeyFromHeaders(requestHeaders), getLangKeyFromHeaders(requestHeaders))
  if (storeBaseLoadedByDomain.get(domainKey) && isFreshLoaded(storeBaseLoadedAtByDomain.get(domainKey))) return
  const inFlight = storeBaseLoadPromiseByDomain.get(domainKey)
  if (inFlight) return inFlight
  const promise = (async () => {
    const count = await loadByScope('storeBase', apiBase, requestHeaders)
    storeBaseLoadedByDomain.set(domainKey, true)
    storeBaseLoadedAtByDomain.set(domainKey, Date.now())
    console.log(`[CMS] 店铺基础组件加载完成，共 ${count} 个`)
  })()
  storeBaseLoadPromiseByDomain.set(domainKey, promise)
  try {
    await promise
  } finally {
    storeBaseLoadPromiseByDomain.delete(domainKey)
  }
}

/**
 * 加载租户组件。
 * 使用 in-flight promise 防止并发重复请求。
 */
export async function loadTenantComponents(apiBase: string = '', requestHeaders?: RequestHeaders) {
  const domainKey = getActiveDomainKey() || buildRegistryKey(getDomainKeyFromHeaders(requestHeaders), getLangKeyFromHeaders(requestHeaders))
  if (tenantLoadedByDomain.get(domainKey) && isFreshLoaded(tenantLoadedAtByDomain.get(domainKey))) return
  const inFlight = tenantLoadPromiseByDomain.get(domainKey)
  if (inFlight) return inFlight
  const promise = (async () => {
    const count = await loadByScope('tenant', apiBase, requestHeaders)
    tenantLoadedByDomain.set(domainKey, true)
    tenantLoadedAtByDomain.set(domainKey, Date.now())
    console.log(`[CMS] 租户组件加载完成，共 ${count} 个，注册表总计 ${getRegistry(domainKey).size} 个`)
  })()
  tenantLoadPromiseByDomain.set(domainKey, promise)
  try {
    await promise
  } finally {
    tenantLoadPromiseByDomain.delete(domainKey)
  }
}

/**
 * 获取单个组件定义。
 * 通过读取 registryVersion.value 建立 Vue 响应式追踪，
 * 确保当注册表被填充时，依赖此函数的 computed 自动重新计算。
 */
export function getComponentDefinition(name: string): ComponentDefinition | undefined {
  void registryVersion.value
  const activeKey = getActiveDomainKey()
  if (activeKey) {
    const reg = registryByDomain.get(activeKey)
    const found = reg?.get(name)
    if (found) return found
  }
  const reg = registryByDomain.get('')
  const fallback = reg?.get(name)
  if (fallback) return fallback
  for (const [, r] of registryByDomain) {
    const item = r.get(name)
    if (item) return item
  }
  return undefined
}

/** 获取所有组件定义 */
export function getAllDefinitions(): ComponentDefinition[] {
  void registryVersion.value
  const activeKey = getActiveDomainKey()
  if (activeKey) return Array.from(getRegistry(activeKey).values())
  return Array.from(getRegistry('').values())
}

/** 按分类获取 */
export function getDefinitionsByCategory(category: string): ComponentDefinition[] {
  return getAllDefinitions().filter(d => d.category === category)
}

/** 按 scope 获取 */
export function getDefinitionsByScope(scope: ComponentScope): ComponentDefinition[] {
  return getAllDefinitions().filter(d => d.scope === scope)
}

/** 检查某个组件是否有 Schema 定义 */
export function hasSchemaDefinition(componentName: string): boolean {
  const activeKey = getActiveDomainKey()
  if (activeKey) return getRegistry(activeKey).has(componentName)
  return getRegistry('').has(componentName)
}

/** 检查基础组件是否已加载 */
export function isBaseLoaded(): boolean {
  return baseLoadedByDomain.get(getActiveDomainKey()) === true
}

/** 检查店铺基础组件是否已加载 */
export function isStoreBaseLoaded(): boolean {
  return storeBaseLoadedByDomain.get(getActiveDomainKey()) === true
}

/** 检查租户组件是否已加载 */
export function isTenantLoaded(): boolean {
  return tenantLoadedByDomain.get(getActiveDomainKey()) === true
}

export async function loadComponentDefinitions(apiBase: string = '', requestHeaders?: RequestHeaders) {
  await Promise.all([
    loadBaseComponents(apiBase, requestHeaders),
    loadStoreBaseComponents(apiBase, requestHeaders),
  ])
}

export function isRegistryLoaded(): boolean {
  const activeKey = getActiveDomainKey()
  return baseLoadedByDomain.get(activeKey) === true && storeBaseLoadedByDomain.get(activeKey) === true
}

/**
 * 提取精简渲染字段，用于客户端 hydration 渲染。
 * 剔除所有编辑器相关的元信息（icon / category / displayName / scope 等），
 * 减小 payload 大小。
 */
export function pickRuntimeDefinition(def: ComponentDefinition): Partial<ComponentDefinition> {
  return {
    component: def.component,
    htmlTemplate: def.htmlTemplate,
    css: def.css,
    jsLogic: def.jsLogic,
    hasChildren: def.hasChildren,
    defaultVariables: def.defaultVariables,
    globalStyleConfig: def.globalStyleConfig,
    autoFetch: def.autoFetch,
  }
}

/**
 * 使用服务端 payload 注水组件定义，避免客户端重复请求接口。
 */
export function hydrateDefinitions(items: any[]) {
  if (!Array.isArray(items) || !items.length) return
  const domainKey = getActiveDomainKey()
  const registry = getRegistry(domainKey)
  let count = 0
  for (const item of items) {
    // 兼容可能已经是精简版的 Partial<ComponentDefinition> 或者完整原始定义
    const definition: ComponentDefinition = {
      component: item.component,
      displayName: item.displayName || item.component,
      htmlTemplate: item.htmlTemplate,
      css: item.css,
      jsLogic: item.jsLogic,
      hasChildren: item.hasChildren,
      defaultVariables: item.defaultVariables || {},
      globalStyleConfig: item.globalStyleConfig,
      autoFetch: item.autoFetch,
      schema: item.schema || [],
      scope: item.scope || 'base',
    }
    registry.set(definition.component, definition)
    count++
  }
  if (count > 0) registryVersion.value++
  // payload 来自服务端全量加载，客户端可视作已完成加载
  baseLoadedByDomain.set(domainKey, true)
  storeBaseLoadedByDomain.set(domainKey, true)
  tenantLoadedByDomain.set(domainKey, true)
  const now = Date.now()
  baseLoadedAtByDomain.set(domainKey, now)
  storeBaseLoadedAtByDomain.set(domainKey, now)
  tenantLoadedAtByDomain.set(domainKey, now)
}

export function invalidateRegistry(domain?: string): void {
  const key = normalizeDomainKey(domain) || getActiveDomainKey()
  if (key) {
    registryByDomain.delete(key)
    baseLoadedByDomain.delete(key)
    storeBaseLoadedByDomain.delete(key)
    tenantLoadedByDomain.delete(key)
    baseLoadedAtByDomain.delete(key)
    storeBaseLoadedAtByDomain.delete(key)
    tenantLoadedAtByDomain.delete(key)
    baseLoadPromiseByDomain.delete(key)
    storeBaseLoadPromiseByDomain.delete(key)
    tenantLoadPromiseByDomain.delete(key)
    registryVersion.value++
    return
  }
  registryByDomain.clear()
  baseLoadedByDomain.clear()
  storeBaseLoadedByDomain.clear()
  tenantLoadedByDomain.clear()
  baseLoadedAtByDomain.clear()
  storeBaseLoadedAtByDomain.clear()
  tenantLoadedAtByDomain.clear()
  baseLoadPromiseByDomain.clear()
  storeBaseLoadPromiseByDomain.clear()
  tenantLoadPromiseByDomain.clear()
  registryVersion.value++
}
